import { Injectable } from '@angular/core';
import { IBaseElement, ISeat, ISeatPayload } from '../interfaces';
import { FlattenSeat, GuidWrapper } from '../types';
import { Guid } from 'guid-typescript';
import { ShapeKeys, ShapeSeatTypes } from '../enums';
import { IDictionaryValue } from '@cinetixx/cinetixx-ui';
import { CreatorStateService } from '../services/app-services';
import { ShapeAttributesManager } from './shape-attributes.manager';
import { DEFAULT_SEAT_DATA, SEAT_TYPE_DISABLED_ID, SEAT_TYPE_SEAT_ID, SEAT_TYPE_WHEELCHAIR_ID } from '../constants';
import { HTMLElementUtils, ObjectUtils } from '@cinetixx/cinetixx-ui';
import { TextFormattingUtils } from '../utils';

@Injectable()
export class SeatManager {

  public static getHTMLElement(guid: string, container?: HTMLElement): HTMLElement {
    return (container ?? CreatorStateService.roomRef.nativeElement).querySelector<HTMLElement>(
      `[${ShapeAttributesManager.getAttribute(ShapeKeys.GUID)}="${guid}"]`
    ) || null;
  }

  public static getSeat(guid: string): GuidWrapper<ISeat> {
    return CreatorStateService.seatsPlan.seats.find((seat) => seat.guid === guid) ?? null;
  }

  public static getSeatByDefinitionId(id: number): GuidWrapper<ISeat> {
    return id ? (CreatorStateService.seatsPlan.seats.find((seat) => seat.id === id) ?? null) : null;
  }

  public static transformHTMLElementToObject(el: HTMLElement): GuidWrapper<ISeat> {
    const seat = CreatorStateService.seatsPlan.seats.find((seat) => seat.guid === ShapeAttributesManager.getGUID(el));
    const shape = CreatorStateService.shapes.find(x => x.id === ShapeAttributesManager.getShapeId(el));

    const baseData = {
      webX: parseInt(el.style.left),
      webY: parseInt(el.style.top),
      w: el.style.width ? parseInt(getComputedStyle(el).width) : Math.max(25, el.offsetWidth),
      h: el.style.height ? parseInt(getComputedStyle(el).height) : el.offsetHeight,
      color: ShapeAttributesManager.getShapeBackgroundColor(el),
      shapeId: shape.id,
      rotate: HTMLElementUtils.getRotationDegrees(el.querySelector('.shape .editable-clip-path')),
    } as IBaseElement;

    if (seat) {
      return SeatManager.formatObject({
        ...seat,
        sectorElement: {
          ...seat.sectorElement,
          ...baseData
        },
        guid: ShapeAttributesManager.getGUID(el)
      });
    }

    return SeatManager.formatObject({
      sectorElement: baseData,
      seatTypeId: SeatManager.mapShapeSeatType(shape.shapeSeatType)?.id,
      guid: ShapeAttributesManager.getGUID(el)
    });
  }

  public static getDataToSend(seat: Partial<ISeat>): ISeatPayload {
    const shape = CreatorStateService.shapes.find(x => x.id === seat.sectorElement.shapeId);
    const flattenSeat = ObjectUtils.flat(seat) as FlattenSeat;
    const ruleTopText = seat.sectorElement.ruleTopText || DEFAULT_SEAT_DATA.sectorElement.ruleTopText;
    const ruleBottomText = seat.sectorElement.ruleBottomText || DEFAULT_SEAT_DATA.sectorElement.ruleBottomText;
    const ruleTipText = seat.sectorElement.ruleTipText || DEFAULT_SEAT_DATA.sectorElement.ruleTipText;
    const ruleTitle = seat.sectorElement.ruleTitle || DEFAULT_SEAT_DATA.sectorElement.ruleTitle;

    return {
      ...SeatManager.getBaseObject(seat),
      x: seat.sectorElement.x,
      webX: seat.sectorElement.webX,
      y: seat.sectorElement.y,
      webY: seat.sectorElement.webY,
      width: seat.sectorElement.w,
      height: seat.sectorElement.h,
      color: seat.sectorElement.color,
      flagSeatFac: seat.sectorElement.flagSeatFac ?? DEFAULT_SEAT_DATA.sectorElement.flagSeatFac,
      flagRankFac: seat.sectorElement.flagRankFac ?? DEFAULT_SEAT_DATA.sectorElement.flagRankFac,
      title: TextFormattingUtils.formatText(ruleTitle, flattenSeat) ?? DEFAULT_SEAT_DATA.sectorElement.title,
      titleSizeMin: DEFAULT_SEAT_DATA.sectorElement.titleSizeMin,
      titleSizeMax: DEFAULT_SEAT_DATA.sectorElement.titleSizeMax,
      image: shape.image,
      imageX: shape.imageX,
      imageY: shape.imageY,
      sectorId: seat.sectorElement.sectorId ?? null,
      shapeId: seat.sectorElement.shapeId || null,
      rotate: seat.sectorElement.rotate || 0,
      seatConfigurationId: CreatorStateService.seatsPlan.seatConfigurationId,
      seatDefinitionId: +seat.id || null,
      seatName: '',
      seatTypeId: seat.seatTypeId ?? SeatManager.mapShapeSeatType(shape.shapeSeatType)?.id,
      sectorElementId: seat?.sectorElement?.id || null,
      rankToken: seat.rankToken ?? '0',
      ruleTitle,
      ruleTopText,
      bottomText: TextFormattingUtils.formatText(ruleBottomText, flattenSeat) || DEFAULT_SEAT_DATA.sectorElement.bottomText,
      ruleBottomText,
      tipText: TextFormattingUtils.formatText(ruleTipText, flattenSeat) || DEFAULT_SEAT_DATA.sectorElement.tipText,
      ruleTipText,
      topText: TextFormattingUtils.formatText(ruleTopText, flattenSeat) || DEFAULT_SEAT_DATA.sectorElement.topText,
      loveSeatId: seat.loveSeatId ?? DEFAULT_SEAT_DATA.loveSeatId
    } as ISeatPayload;
  }

  public static formatObject(seat: Partial<GuidWrapper<ISeat>>): GuidWrapper<ISeat> {
    const shapeId = seat.sectorElement.shapeId ?? DEFAULT_SEAT_DATA.sectorElement.shapeId;
    let seatTypeId = DEFAULT_SEAT_DATA.seatTypeId;

    if (!seat.seatTypeId) {
      const shape = CreatorStateService.shapes.find(x => x.id === shapeId);

      if (shape) {
        seatTypeId = SeatManager.mapShapeSeatType(shape.shapeSeatType)?.id;
      }
    }

    return {
      ...SeatManager.getBaseObject(seat),
      guid: seat.guid ?? Guid.raw(),
      sectorElement: {
        shapeId,
        x: seat.sectorElement.x ?? DEFAULT_SEAT_DATA.sectorElement.x,
        webX: seat.sectorElement.webX ?? DEFAULT_SEAT_DATA.sectorElement.webX,
        y: seat.sectorElement.y ?? DEFAULT_SEAT_DATA.sectorElement.y,
        webY: seat.sectorElement.webY ?? DEFAULT_SEAT_DATA.sectorElement.webY,
        w: seat.sectorElement.w ?? DEFAULT_SEAT_DATA.sectorElement.w,
        h: seat.sectorElement.h ?? DEFAULT_SEAT_DATA.sectorElement.h,
        title: seat.sectorElement.title ?? DEFAULT_SEAT_DATA.sectorElement.title,
        color: seat.sectorElement.color ?? DEFAULT_SEAT_DATA.sectorElement.color,
        flagRankFac: seat.sectorElement.flagRankFac ?? DEFAULT_SEAT_DATA.sectorElement.flagRankFac,
        flagSeatFac: seat.sectorElement.flagSeatFac ?? DEFAULT_SEAT_DATA.sectorElement.flagSeatFac,
        alignCurveId: seat.sectorElement.alignCurveId ?? DEFAULT_SEAT_DATA.sectorElement.alignCurveId,
        alignCurvePosition: seat.sectorElement.alignCurvePosition ?? DEFAULT_SEAT_DATA.sectorElement.alignCurvePosition,
        rotate: seat.sectorElement.rotate ?? DEFAULT_SEAT_DATA.sectorElement.rotate,
        seatConfigurationId: seat.sectorElement.seatConfigurationId ?? CreatorStateService.seatsPlan.seatConfigurationId,
        titleSizeMax: seat.sectorElement.titleSizeMax ?? DEFAULT_SEAT_DATA.sectorElement.titleSizeMax,
        titleSizeMin: seat.sectorElement.titleSizeMin ?? DEFAULT_SEAT_DATA.sectorElement.titleSizeMin,
        image: seat.sectorElement.image ?? DEFAULT_SEAT_DATA.sectorElement.image,
        imageX: seat.sectorElement.imageX ?? DEFAULT_SEAT_DATA.sectorElement.imageX,
        imageY: seat.sectorElement.imageY ?? DEFAULT_SEAT_DATA.sectorElement.imageY,
        id: seat.sectorElement.id ?? DEFAULT_SEAT_DATA.sectorElement.id,
        ruleTitle: seat.sectorElement.ruleTitle ?? DEFAULT_SEAT_DATA.sectorElement.ruleTitle,
        ruleTopText: seat.sectorElement.ruleTopText ?? DEFAULT_SEAT_DATA.sectorElement.ruleTopText,
        bottomText: seat.sectorElement.bottomText || DEFAULT_SEAT_DATA.sectorElement.bottomText,
        ruleBottomText: seat.sectorElement.ruleBottomText ?? DEFAULT_SEAT_DATA.sectorElement.ruleBottomText,
        tipText: seat.sectorElement.tipText ?? DEFAULT_SEAT_DATA.sectorElement.tipText,
        ruleTipText: seat.sectorElement.ruleTipText ?? DEFAULT_SEAT_DATA.sectorElement.ruleTipText,
        topText: seat.sectorElement.topText ?? DEFAULT_SEAT_DATA.sectorElement.topText,
        sectorId: seat.sectorElement.sectorId ?? DEFAULT_SEAT_DATA.sectorElement.sectorId,
      },
      rankToken: seat.rankToken ?? DEFAULT_SEAT_DATA.rankToken,
      seatTypeId: seat.seatTypeId ?? seatTypeId,
      termId: seat.termId ?? DEFAULT_SEAT_DATA.termId,
      seatCount: seat.seatCount ?? DEFAULT_SEAT_DATA.seatCount,
      areaId: seat.areaId ?? DEFAULT_SEAT_DATA.areaId,
      priceAreaColor: seat.priceAreaColor ?? DEFAULT_SEAT_DATA.priceAreaColor,
      ticketEntryText: seat.ticketEntryText ?? DEFAULT_SEAT_DATA.ticketEntryText,
      name: seat.name ?? DEFAULT_SEAT_DATA.name,
      id: seat.id ?? DEFAULT_SEAT_DATA.id
    } as GuidWrapper<ISeat>;
  }

  public static mapShapeSeatType(shapeSeatType: ShapeSeatTypes): IDictionaryValue {
    switch (shapeSeatType) {
      case ShapeSeatTypes.DISABLED:
        return CreatorStateService.seatTypes.find(x => x.id === SEAT_TYPE_DISABLED_ID);
      case ShapeSeatTypes.WHEELCHAIR:
        return CreatorStateService.seatTypes.find(x => x.id === SEAT_TYPE_WHEELCHAIR_ID);
      default:
        return CreatorStateService.seatTypes.find(x => x.id === SEAT_TYPE_SEAT_ID);
    }
  }

  private static getBaseObject(seat: Partial<GuidWrapper<ISeat>>): Partial<GuidWrapper<ISeat>> {
    return {
      seatNumber: seat?.seatNumber ?? DEFAULT_SEAT_DATA.seatNumber,
      rank: seat?.rank ?? DEFAULT_SEAT_DATA.rank,
      seatStatusId: seat?.seatStatusId ?? DEFAULT_SEAT_DATA.seatStatusId,
      leftSeatDefinitionId: seat?.leftSeatDefinitionId ?? DEFAULT_SEAT_DATA.leftSeatDefinitionId,
      rightSeatDefinitionId: seat?.rightSeatDefinitionId ?? DEFAULT_SEAT_DATA.rightSeatDefinitionId,
      loveSeatId: seat?.loveSeatId ?? DEFAULT_SEAT_DATA.loveSeatId,
      seatWeight: seat.seatWeight ?? DEFAULT_SEAT_DATA.seatWeight
    };
  }
}
